import { useEffect, useState } from "react";
import { Sparkles, Lightbulb, Volume2, VolumeX } from "lucide-react";
import { isVoiceOn, setVoiceOn, speak, primeVoices, stopSpeaking } from "@/utils/jrVoice";

/**
 * LemonBotJr — a static, friendly helper card. No AI calls; each page
 * passes in a short list of guided prompts appropriate to that step.
 *
 * Includes a voice toggle that reads the greeting and prompts aloud
 * using the browser's SpeechSynthesis API — zero backend, zero API key.
 */
export default function LemonBotJr({
  greeting = "Hi, I'm LemonBot Jr.! I'll help you think like a Junior CEO.",
  prompts = [],
  tone = "yellow", // "yellow" | "green"
  compact = false,
  autoSpeak = false,
  testId = "lemonbot-jr",
}) {
  const [voiceOn, setVoiceOnLocal] = useState(isVoiceOn());

  useEffect(() => {
    primeVoices();
    return () => stopSpeaking();
  }, []);

  // Re-speak when the greeting itself changes (e.g., page change) and
  // voice is on, or autoSpeak is requested. Always requires a prior
  // user gesture somewhere in the session to satisfy autoplay rules.
  useEffect(() => {
    if (!autoSpeak) return;
    if (!voiceOn) return;
    const fullText = [greeting, ...prompts].join(". ");
    speak(fullText);
    return () => stopSpeaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greeting]);

  const palette = tone === "green"
    ? {
        bg: "from-green-50 to-emerald-50",
        ring: "ring-green-200",
        chip: "bg-green-500",
        accent: "text-green-800",
        pill: "bg-white ring-green-100",
      }
    : {
        bg: "from-yellow-50 to-amber-50",
        ring: "ring-yellow-200",
        chip: "bg-yellow-500",
        accent: "text-yellow-900",
        pill: "bg-white ring-yellow-100",
      };

  const handleSpeak = () => {
    const fullText = [greeting, ...prompts].join(". ");
    speak(fullText, { force: true });
  };

  const handleToggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    setVoiceOnLocal(next);
    if (next) {
      // Re-speak the current greeting so kids hear the change.
      const fullText = [greeting, ...prompts].join(". ");
      speak(fullText);
    }
  };

  return (
    <aside
      className={`bg-gradient-to-br ${palette.bg} rounded-3xl ring-1 ${palette.ring} p-5 sm:p-6 ${compact ? "" : "shadow-sm"}`}
      data-testid={testId}
      aria-label="LemonBot Jr. helper"
    >
      <div className="flex items-start gap-4">
        <div
          className={`${palette.chip} w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shadow-sm shrink-0 relative overflow-hidden`}
          aria-hidden="true"
        >
          <img
            src="/elementary/mascot/lemonbot-jr.png"
            alt=""
            className="w-full h-full object-contain p-0.5"
            loading="lazy"
            decoding="async"
          />
          <span className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow">
            <Sparkles className="w-3 h-3 text-yellow-500" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className={`text-xs font-bold uppercase tracking-wide ${palette.accent}`}>
              LemonBot Jr.
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleSpeak}
                className="text-xs font-bold text-yellow-800 hover:text-yellow-900 bg-white/80 hover:bg-white ring-1 ring-yellow-200 hover:ring-yellow-400 rounded-full px-2.5 py-1 inline-flex items-center gap-1 transition-all"
                data-testid={`${testId}-speak`}
                aria-label="Read aloud"
              >
                <Volume2 className="w-3.5 h-3.5" />
                Read
              </button>
              <button
                type="button"
                onClick={handleToggleVoice}
                className="text-xs font-bold text-gray-600 hover:text-gray-900 bg-white/60 hover:bg-white ring-1 ring-gray-200 hover:ring-gray-400 rounded-full p-1.5 transition-all"
                data-testid={`${testId}-voice-toggle`}
                aria-label={voiceOn ? "Mute voice" : "Unmute voice"}
                aria-pressed={!voiceOn}
              >
                {voiceOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <p className="text-gray-800 text-base font-medium leading-relaxed">
            {greeting}
          </p>
          {prompts.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid={`${testId}-prompts`}>
              {prompts.map((p, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-2 ${palette.pill} rounded-xl px-3 py-2 ring-1 text-sm text-gray-800`}
                >
                  <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500 italic">
            I help you think — your answers are all yours!
          </p>
        </div>
      </div>
    </aside>
  );
}
